import { notify } from 'app/services/errorReporter';
import FluentdLogger from 'app/services/fluentdLogger';
import { parseStorageValue } from 'app/models/storage/selectors';
import { debug } from 'app/services/logger';

/**
 * Returns an action creator that initialises the storage
 *
 * @param {Record} Storage Contains keys that should be used to read from the native storage
 * @param {Function} getStorage The function to access the native local or session Storage
 * @param {Function} initAction The initAction creator, that will trigger the init reducer
 * @param {Function} getItem The getItem selector
 */
export const init = (
  Storage,
  getStorage,
  initAction,
  getItem,
) => () => (dispatch, getState) => {
  const storageCopy = {};
  let wasStorageInitiallyAvailable = false;

  try {
    for (const key of Storage.keys()) {
      const jsonValue = getStorage().getItem(key);
      if (jsonValue != null) {
        storageCopy[key] = parseStorageValue(key, jsonValue);
      }
    }

    wasStorageInitiallyAvailable = true;
  } catch (error) {
    FluentdLogger.info({
      name: `StorageError: ${error.name}`,
      message: error.message,
    });
  }

  dispatch(initAction(wasStorageInitiallyAvailable, storageCopy));
};

/**
 * Returns an action creator that sets items on the storage
 *
 * @param {Function} validateKey function that checks if the key is allowed to be used
 * @param {Function} wasStorageInitiallyAvailable selector, that returns if the native storage was
 *  available
 * @param {Function} getStorage The function to access the native local or session Storage
 * @param {Function} setItemAction The setItemAction creator, that will trigger the setItem reducer
 */
export const setItem = (
  validateKey,
  wasStorageInitiallyAvailable,
  getStorage,
  setItemAction,
) => (key, value) => (dispatch, getState) => {
  validateKey(key, { key, value });

  let jsonValue;

  try {
    jsonValue = JSON.stringify(value);
  } catch (error) {
    notify(error, {
      name: 'Storage.couldntStringifyValue',
      context: { key },
    });
    throw error;
  }

  const action = setItemAction(key, value);

  debug('Storage.setItem', action);

  dispatch(action);

  if (wasStorageInitiallyAvailable(getState())) {
    try {
      getStorage().setItem(key, jsonValue);
    } catch (error) {
      FluentdLogger.info({
        name: `StorageError: ${error.name}`,
        message: error.message,
      });
    }
  }
};

/**
 *  Returns an action creator that removes an item from the storage
 *
 * @param {Function} validateKey function that checks if the key is allowed to be used
 * @param {Function} wasStorageInitiallyAvailable selector, that returns if the native storage was
 *  available
 * @param {Function} getStorage The function to access the native local or session Storage
 * @param {Function} removeItemAction The removeItemAction creator, that will trigger the removeItem
 *  reducer
 */
export const removeItem = (
  validateKey,
  wasStorageInitiallyAvailable,
  getStorage,
  removeItemAction,
) => (key) => (dispatch, getState) => {
  validateKey(key, { key });

  if (wasStorageInitiallyAvailable(getState())) {
    try {
      getStorage().removeItem(key);
    } catch (error) {
      FluentdLogger.info({
        name: `StorageError: ${error.name}`,
        message: error.message,
      });
    }
  }

  dispatch(removeItemAction(key));
};
